import { useRef } from 'react'
import Add from '@mui/icons-material/Add'
import { Box, Button, Stack } from '@mui/material'
import useGetThemeColor from '@/hooks/useGetThemeColor'
import useOverlay from '@/hooks/useOverlay'
import useGetEventsForWorkflowState from '@/services/hooks/useGetEventsForWorkflowState'
import LargeHeading from '@/components/large-heading/LargeHeading'
import AddEditWorkflowStateEventDialog from '@/components/workflow-state-page/AddEditWorkflowStateEventDialog'
import { useWorkflowStatePageContext } from '@/components/workflow-state-page/WorkflowStatePage'
import AddSystemEventMenu from './AddSystemEventMenu'
import EventLanes from './EventLanes'

export default function WorkflowStateEvents() {
  const getThemeColor = useGetThemeColor()
  const { workflowState } = useWorkflowStatePageContext()

  const containerRef = useRef<HTMLDivElement>(null)

  const addEditOverlay = useOverlay()
  const addSystemEventOverlay = useOverlay()

  const { systemEvents, customEvents, isPending, queryKey } =
    useGetEventsForWorkflowState()

  if (isPending) return null

  const existingSystemEventCodes = systemEvents.map((event) => event.code)

  return (
    <>
      <Stack spacing={2}>
        <LargeHeading
          heading={
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              flexShrink={0}
            >
              <Box
                sx={{
                  background: getThemeColor(workflowState.color),
                  width: 4,
                  height: 24,
                  borderRadius: 4,
                }}
              />
              <Box>{workflowState.name}</Box>
            </Stack>
          }
        />

        <Box ref={containerRef}>
          <Stack
            direction="column"
            spacing={4}
            sx={{
              minWidth: '100%',
            }}
          >
            <EventLanes
              title={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Box>System Events</Box>
                  <Button
                    variant="text"
                    startIcon={<Add />}
                    onClick={addSystemEventOverlay.open}
                  >
                    Add Event
                  </Button>
                </Stack>
              }
              workflowStateEvents={systemEvents}
              listQueryKey={queryKey}
            />
            <EventLanes
              isReorderable
              showPrimaryEvent
              title={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Box>Custom Events</Box>
                  <Button
                    variant="text"
                    startIcon={<Add />}
                    onClick={addEditOverlay.open}
                  >
                    Add Event
                  </Button>
                </Stack>
              }
              workflowStateEvents={customEvents}
              listQueryKey={queryKey}
            />
          </Stack>
        </Box>
      </Stack>

      <AddEditWorkflowStateEventDialog
        overlay={addEditOverlay}
        workflowState={workflowState}
        listQueryKey={queryKey}
        onSave={() => {
          setTimeout(() => {
            const scrollContainer = containerRef.current?.closest(
              '[data-scroll-container]',
            )
            scrollContainer?.scrollTo({
              top: scrollContainer.scrollHeight,
              behavior: 'smooth',
            })
          }, 100)
        }}
      />

      <AddSystemEventMenu
        overlay={addSystemEventOverlay}
        workflowState={workflowState}
        listQueryKey={queryKey}
        existingCodes={existingSystemEventCodes}
      />
    </>
  )
}
